import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Button from '../Button';
import Alert from '@material-ui/lab/Alert';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import StarRating from '../widgets/StarRating';
import { ReviewAPI } from '../../api';
import * as track from '../../lib/track';

const useStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: 0,
  },
  row: {
    width: '100%',
    padding: '0.5rem 0',
  },
  alert: {
    width: '100%',
    marginTop: '0.5rem',
  },
  button: {
    margin: '1rem 1rem 1rem 0',
  },
  label: {
    width: '100%',
    marginBottom: '1rem',
  },
});

const ReviewForm = ({onClose=null, completedAction, providerUuid, itemType, itemUuid}) => {
  const classes = useStyles();
  const [busy, setBusy] = React.useState(false);
  const [content, setContent] = React.useState('');
  const [rating, setRating] = React.useState(null);
  const [contentError, setContentError] = React.useState(null);
  const [ratingError, setRatingError] = React.useState(null);
  const [submissionError, setSubmissionError] = React.useState(null);

  const validateContent = () => {
    const contentLength = content.length;
    const spaceCount = (content.split(' ').length - 1);
    if (contentLength > 10 && spaceCount > 1) {
      setContentError(null);
      return true;
    } else {
      setContentError('You must write at least 3 words in your review.');
      return false;
    }
  };

  const validateRating = () => {
    const ratingValid = rating !== null;
    if (ratingValid) {
      setRatingError(null);
      return true;
    } else {
      setRatingError('You must provide a rating.');
      return false;
    }
  };

  const submit = async () => {
    setBusy(true);
    if (validateContent() && validateRating()) {
      try {
        const { payload } = await ReviewAPI.createReview(providerUuid, {
          content: content,
          rating: rating,
          item_type: itemType,
          item_uuid: itemUuid,
        });
        setBusy(false);
        track.sendEvent('review form', 'submit', {provider_uuid: providerUuid});
        completedAction(payload.uuid);
      } catch (err) {
        setSubmissionError('Oh no! We were unable to submit your review due to technical problems. Please try again later or contact us.');
        setBusy(false);
      }
    } else {
      setBusy(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <Typography variant="subtitle2" color="primary" className={classes.label}>
          Please rate your experience:
        </Typography>
        <StarRating
          rating={parseInt(rating)}
          readonly={false}
          onChange={(event) => setRating(event.target.value)}
          name="review-rating"
          precision={1}
        />
        {ratingError === null ? null : (
          <Alert severity="error" className={classes.alert}>
            {ratingError}
          </Alert>
        )}
      </div>
      <div className={classes.row}>
        <Typography variant="subtitle2" color="primary" className={classes.label}>
          Please tell us about your experience:
        </Typography>
        <TextField
          fullWidth
          required
          InputLabelProps={{
            shrink: true,
          }}
          value={content}
          variant="outlined"
          multiline
          rows={5}
          onChange={(event) => setContent(event.target.value)}
        />
        {contentError === null ? null : (
          <Alert severity="error" className={classes.alert}>
            {contentError}
          </Alert>
        )}
      </div>
      <div className={classes.row}>
        {onClose === null ? null : (
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            label="Cancel"
            className={classes.button}
            pointer="none"
          />
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={submit}
          label="Submit"
          busy={busy}
          className={classes.button}
          pointer="right"
        />
        {submissionError === null ? null : (
          <Alert severity="error" className={classes.alert}>
            {submissionError}
          </Alert>
        )}
      </div>
    </div>
  );
};

ReviewForm.propTypes = {
  onClose: PropTypes.func,
  completedAction: PropTypes.func.isRequired,
  providerUuid: PropTypes.string.isRequired,
  itemType: PropTypes.string.isRequired,
  itemUuid: PropTypes.string.isRequired,
};

export default ReviewForm;
